import clsx from 'clsx';
import { HTMLAttributes, ReactNode } from 'react';

type HeadingSize = 'xxl' | 'xl' | 'l' | 'm' | 's' | 'xs';
type HeadingTag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

type Props = {
  size?: HeadingSize;
  level?: HeadingTag;
  children: ReactNode;
  /**
   * Based on Tailwind colors
   * @see https://tailwindcss.com/docs/colors
   */
  color?: string;
} & HTMLAttributes<HTMLHeadingElement>;

const headingStyles: Record<HeadingSize, string> = {
  xxl: 'text-[40px] leading-[52px]',
  xl: 'text-[32px] leading-10',
  l: 'text-2xl',
  m: 'text-lg leading-6',
  s: 'text-base',
  xs: 'text-xs uppercase',
};

const defaultTagForSize: Record<HeadingSize, HeadingTag> = {
  xxl: 'h1',
  xl: 'h2',
  l: 'h3',
  m: 'h4',
  s: 'h5',
  xs: 'h6',
};

export const Heading = ({
  size = 'l',
  level,
  children,
  className,
  color,
  ...props
}: Props) => {
  const Tag = level || defaultTagForSize[size];

  return (
    <Tag
      {...props}
      className={clsx(
        color || 'text-gray-900',
        'font-bold',
        headingStyles[size],
        className,
      )}
    >
      {children}
    </Tag>
  );
};
